import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../PanelContainer.scss';
import { PanelCommonProps } from 'config/panelConfig';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { usePanelContainer } from '../usePanelContainer';
import { MarkerLinesMoveRender, useBufferPool } from './vtkManagers';
import {
  BranchInfo,
  calcStenosisText,
  splitNarrativeLines,
} from 'utils/FunctionUtilites';
import ISRReportTable from '@components/ReportTable/ISRReportTable';
import { useBranchInfo } from '@contexts/BranchContext';
import PanelOverlay from './PanelOverlay';
import {
  renderers,
  scheduleRender,
} from '@features/analysis/rendering/Panels/renderState';
import StenosisTable, {
  SegmentRow,
  SegmentSelection,
  SegmentTargets,
} from '@components/ReportTable/StenosisTable';
import { CalciumAnalysisResult } from '@models/Parameter';
import useAuth from '@hooks/Auth/useAuth';
import { isr_function } from 'utils/ISRUtilities';
import { useConfig } from 'config/useConfig';
import { ISRDataProp } from 'config/config';
import useModalActions from '@hooks/common/useModalAction';
import PlaqueTable from '@components/ReportTable/PlaqueTable';
import TaviReportTable, {
  TaviReportSection,
} from '@components/ReportTable/TaviReportTable';
import { getTaviParsedData } from './taviDataUtils';

const ReportPanel: React.FC<PanelCommonProps> = ({
  className,
  panelConfig,
}: PanelCommonProps) => {
  const {
    fullscreenPanelComponentKey,
    updateSelectedPanelConfig,
    panelStates,
    analysisType,
    updateCPRState,
  } = usePanelContainer();
  const { switchConfigs, globalToolState } = useAnalysisPage();
  const { selectedSeriesId } = useBrowserPage();
  const { branchIds } = useBranchInfo();
  const { bufferPool } = useBufferPool();
  const { waitingAction, closeLoadingModal } = useModalActions();

  const BranchId = panelStates?.['CPR']?.coronary?.current?.[0];
  const BranchData = panelStates?.['CPR']?.coronary?.state?.[BranchId!];

  const { accessToken } = useAuth();
  const { config, setData } = useConfig();

  const [pmdNode, setPmdNode] = useState<{
    proximal: number;
    mid: number;
    distal: number;
  }>({ proximal: -1, mid: -1, distal: -1 });
  const [selectedSegment, setSelectedSegment] =
    useState<SegmentSelection | null>(null);
  const [previewOverride, setPreviewOverride] = useState<{
    branch: string;
    segment: 'proximal' | 'mid' | 'distal';
    value: string;
  } | null>(null);
  const skipPreviewRef = useRef(0);

  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;

  const taviSections = useMemo<TaviReportSection[] | undefined>(() => {
    if (analysisType !== 'tavi' || !pool) return undefined;
    const parsed = getTaviParsedData(pool);
    const avg = (a: number | null, b: number | null) =>
      a !== null && b !== null ? (a + b) / 2 : null;

    const annulus = parsed?.annulus ?? null;
    const stj = parsed?.sinotubularjunction ?? null;
    const ostium = parsed?.ostium
      ? {
          lca:
            parsed.ostium.lca?.length ??
            parsed.ostium.lca?.metric ??
            null,
          rca:
            parsed.ostium.rca?.length ??
            parsed.ostium.rca?.metric ??
            null,
        }
      : null;
    const measurements = parsed?.measurements ?? null;

    const lcc = measurements?.LCC_len ?? null;
    const rcc = measurements?.RCC_len ?? null;
    const ncc = measurements?.NCC_len ?? null;
    const avgValsalva =
      lcc !== null && rcc !== null && ncc !== null ? (lcc + rcc + ncc) / 3 : null;

    return [
      {
        title: 'Annulus',
        rows: [
          { label: 'Short Axis', value: annulus?.minDiameter ?? null, suffix: 'mm' },
          { label: 'Long Axis', value: annulus?.maxDiameter ?? null, suffix: 'mm' },
          { label: 'Area', value: annulus?.areaSize ?? null, suffix: ' mm3/g' },
          { label: 'Perimeter', value: annulus?.areaPerimeter ?? null, suffix: 'mm' },
          {
            label: '(Long Axis + Short Axis) / 2',
            value: avg(annulus?.maxDiameter ?? null, annulus?.minDiameter ?? null),
            suffix: 'mm',
          },
        ],
      },
      {
        title: 'Sinotubular Junction',
        rows: [
          { label: 'Short Axis', value: stj?.minDiameter ?? null, suffix: 'mm' },
          { label: 'Long Axis', value: stj?.maxDiameter ?? null, suffix: 'mm' },
          {
            label: '(Long Axis + Short Axis) / 2',
            value: avg(stj?.maxDiameter ?? null, stj?.minDiameter ?? null),
            suffix: 'mm',
          },
        ],
      },
      {
        title: 'Sinus of Valsalva',
        rows: [
          { label: 'Left coronary cusp diameter', value: lcc, suffix: 'mm' },
          { label: 'Right coronary cusp diameter', value: rcc, suffix: 'mm' },
          { label: 'Non-coronary cusp diameter', value: ncc, suffix: 'mm' },
          { label: 'Average diameter', value: avgValsalva, suffix: 'mm' },
        ],
      },
      {
        title: 'Distances',
        rows: [
          { label: 'LM height', value: ostium?.lca ?? null, suffix: 'mm' },
          { label: 'RCA height', value: ostium?.rca ?? null, suffix: 'mm' },
          { label: 'STJ height', value: measurements?.STJ_height ?? null, suffix: 'mm' },
        ],
      },
    ];
  }, [analysisType, pool]);

  const dataset = useMemo(() => {
    const raw = pool?.branchGraphDataset?.data ?? null;
    return raw instanceof BranchInfo ? raw : null;
  }, [pool?.branchGraphDataset?.data]);
  const branchIdByName = useMemo(() => {
    const paramData = pool?.parameter.data as CalciumAnalysisResult | undefined;
    const name = paramData?.SavedInfo?.Coronary?.newType;
    const map = new Map<string, number>();
    if (Array.isArray(name)) {
      name.forEach((entry) => {
        const [branch, index] = entry as [string, number];
        if (typeof index === 'number' && typeof branch === 'string') {
          map.set(branch, index);
        }
      });
    }
    return map;
  }, [pool?.parameter.data]);

  renderers[panelConfig.componentKey] = {
    render: async (args?: any) => {
      const { type } = args;
      //console.log(args)
      if (type === 'markerMove' || type === 'polygonMove') {
        const { which, newIndex, newPickedPoints } = args;
        const newPmdNode = { ...pmdNode };
        if (which === 'total') {
          for (let i = 0; i < newPickedPoints.length; i++) {
            if (newPickedPoints[i] === null) continue;
            if (i === 0) {
              newPmdNode.proximal = newPickedPoints[i].index;
            } else if (i === 1) {
              newPmdNode.mid = newPickedPoints[i].index;
            } else if (i === 2) {
              newPmdNode.distal = newPickedPoints[i].index;
            }
          }
        } else {
          if (which === 0) newPmdNode.proximal = newIndex;
          else if (which === 1) newPmdNode.mid = newIndex;
          else if (which === 2) newPmdNode.distal = newIndex;
        }
        setPmdNode(newPmdNode);
      }
      if (type === 'stenosisUpdate') {
        waitingAction({ message: 'Calculating' });
        const { result, persent } = args;
        const lesion = config.stenosisLesionInfo;
        const nextLesion = Array.isArray(lesion)
          ? lesion.map((branch) =>
              Array.isArray(branch)
                ? branch.map((entry) =>
                    Array.isArray(entry) ? [...entry] : entry,
                  )
                : branch,
            )
          : [];
        const paramData = pool?.parameter.data as CalciumAnalysisResult;
        const name = paramData?.SavedInfo?.Coronary?.newType;
        let samebranch = '';
        let branchidx = 0;
        persent.forEach((val: any) => {
          if (samebranch !== val.branch) {
            samebranch = val.branch;
            branchidx = 0;
          } else {
            branchidx++;
          }
          let id = -1;
          name.forEach((data) => {
            const [name, num] = data;
            if (val.branch === name) id = num;
          });
          if (id !== -1) {
            const data = nextLesion[id];
            const info = data[branchidx];
            const plaque =
              val.tissue === 'mixied'
                ? 0
                : val.tissue === 'calcified'
                  ? 1
                  : val.tissue === 'noncalcified'
                    ? 2
                    : -1;
            nextLesion[id][branchidx] = [
              info[0],
              info[1],
              info[2],
              val.percentMid / 100,
              plaque,
            ];
          }
        });
        setData({
          stenosisResult: result,
          stenosisLesionInfo: nextLesion,
        });

        scheduleRender('Surface3D', {
          type: 'stenosisUpdate',
        });

        scheduleRender('VR', {
          type: 'stenosisUpdate',
        });

        // if (selectedSeriesId) {
        //   const info: iInterpretation = {
        //     series_id: selectedSeriesId,
        //     json: {
        //       LESION: lesion,
        //     },
        //   };
        //   const result = await send_interpretation_info(accessToken, info);
        //   console.log(result);

        //   const stenosis_status = result[0];
        //   const stenosis_file = result[1];

        //   if (stenosis_status) {
        //     const parsedArray = await Promise.all(
        //       Object.entries(stenosis_file).map(async (values) => {
        //         const cur_path = String(values[1]);
        //         console.log(cur_path);
        //         const { data, status } = await fetchSeriesTargetEx({
        //           accessToken,
        //           seriesId: selectedSeriesId,
        //           target: cur_path,
        //           res: {
        //             Pragma: 'no-cache',
        //             Expires: '0',
        //           },
        //         });
        //         if (status === 200 || status === 201) {
        //           const parsed_data = parse_readNiftiFile(data);
        //           return parsed_data.image as ArrayBuffer;
        //         }
        //       }),
        //     );
        //     const buffers: ArrayBuffer[] = parsedArray.filter(
        //       (buf): buf is ArrayBuffer => buf !== null,
        //     );
        //     scheduleRender('Surface3D', {
        //       type: 'stenosisUpdate',
        //       buffers: buffers,
        //     });
        //   }
        // }
        closeLoadingModal();
      }
      if (type === 'selectStenosis') {
        const { branchName, positionIndex } = args;
        let lesion = config.stenosisLesionInfo;
        const paramData = pool?.parameter.data as CalciumAnalysisResult;
        const name = paramData?.SavedInfo?.Coronary?.newType;
        let id = -1;
        name.forEach((data) => {
          const [name, num] = data;
          if (branchName === name) id = num;
        });
        if (id !== -1) {
          const data2 = lesion[id];
          const info = data2[positionIndex];

          const arr = [
            info[0] + config.coronary_front_expand,

            info[1] + config.coronary_front_expand,
            info[2] + config.coronary_front_expand,
          ];

          updateCPRState({
            mainIndex: { index: 0, branchId: id },
            indexedStates: {
              [id]: arr.map((index) => ({ index })),
            },
          });

          requestAnimationFrame(() => {
            MarkerLinesMoveRender(0, id, [0, 1, 2], arr, pool!);
          });
        }
      }
      if (type === 'reset') {
        if (analysisType === 'stenosis') {
          if (pool) {
            const paramData = pool.parameter.data as CalciumAnalysisResult;
            let lesioninfo = paramData.SavedInfo.Coronary.auto_lesion_info;
            const branchGraphDataset = pool['branchGraphDataset'];
            if (branchGraphDataset.data) {
              lesioninfo = lesioninfo.map((val, index) =>
                val.map((v) => {
                  const mid = (
                    branchGraphDataset.data as BranchInfo
                  ).getStenosisMid(index, v[0], v[1]);
                  const infodata = (
                    branchGraphDataset.data as BranchInfo
                  ).getPlaqueTotalInfo(index, v[0], mid, v[1]);
                  const stenosis = infodata
                    ? Math.max(0, Math.abs((1 - infodata.stenosisDegree) * 100))
                    : v[2];
                  return [v[0], mid, v[1], stenosis, v[3]];
                }),
              );
            }
            const stenosistext = calcStenosisText(
              paramData,
              lesioninfo,
              branchGraphDataset.data as BranchInfo,
            );
            setData({
              stenosisResult: stenosistext,
              stenosisLesionInfo: lesioninfo,
            });
            scheduleRender('Surface3D', {
              type: 'stenosisUpdate',
            });

            scheduleRender('VR', {
              type: 'stenosisUpdate',
            });
          }
        }
        if (analysisType === 'isr') {
          const curr = config.isr_data;
          const nextArr = [...curr];

          for (let i = 0; i < 3; i++) {
            if (curr[i].size !== 0) {
              nextArr[i] = new Map<number, ISRDataProp>();
            }
          }

          setData({ isr_data: nextArr });
        }
      }
    },
  };

  useEffect(() => {
    if (!Array.isArray(BranchData) || BranchData.length < 3) return;
    setPmdNode({
      proximal: BranchData[0].index,
      mid: BranchData[1].index,
      distal: BranchData[2].index,
    });
  }, [
    dataset,
    BranchId,
    // BranchData?.[0]?.index,
    // BranchData?.[1]?.index,
    // BranchData?.[2]?.index,
  ]);

  useEffect(() => {
    const branchId = selectedSegment
      ? branchIdByName.get(selectedSegment.branch)
      : undefined;

    if (!selectedSegment || !dataset || branchId == null) {
      setPreviewOverride(null);
      return;
    }
    if (BranchId == null || BranchId !== branchId) {
      return;
    }
    if (skipPreviewRef.current > 0) {
      skipPreviewRef.current -= 1;
      return;
    }
    if (
      !Number.isFinite(pmdNode.proximal) ||
      !Number.isFinite(pmdNode.mid) ||
      !Number.isFinite(pmdNode.distal)
    ) {
      return;
    }

    const data = dataset.getPlaqueTotalInfo(
      branchId,
      pmdNode.proximal,
      pmdNode.mid,
      pmdNode.distal,
    );
    const stenosisDegree = data?.stenosisDegree;
    if (
      typeof stenosisDegree !== 'number' ||
      !Number.isFinite(stenosisDegree)
    ) {
      return;
    }
    const percentValue = Math.max(0, Math.abs((1 - stenosisDegree) * 100));
    if (!Number.isFinite(percentValue)) {
      return;
    }
    const rounded = Math.round(percentValue * 10) / 10;
    if (!Number.isFinite(rounded)) {
      return;
    }
    const display = rounded.toFixed(1);
    setPreviewOverride({
      branch: selectedSegment.branch,
      segment: selectedSegment.segment,
      value: display.endsWith('.0') ? display.slice(0, -2) : display,
    });
  }, [
    BranchId,
    branchIdByName,
    config.coronary_front_expand,
    config.stenosisLesionInfo,
    dataset,
    pmdNode,
    selectedSegment,
  ]);

  // 테이블 데이터 계산 - useMemo 삭제
  const fmt = (v?: number, d = 1) =>
    typeof v === 'number' && Number.isFinite(v) ? v.toFixed(d) : '-';
  const pct = (num?: number, den?: number, d = 1) =>
    typeof num === 'number' && typeof den === 'number' && den !== 0
      ? `${num.toFixed(d)} mm³ (${((num / den) * 100).toFixed(d)}%)`
      : '-';
  const plaqueFallbackRows = {
    columns: [`Metric`, 'Value'],
    rows: [
      'Length (mm)',
      'Lumen Volume (mm³)',
      'Plaque Volume (mm³)',
      'Vessel Volume (mm³)',
      'Calcified Plaque Volume (mm³, %)',
      'Noncalcified Plaque Volume (mm³, %)',
      'Low Attenuation Plaque Volume (mm³, %)',
      'Remodeling Index',
      'Minimum Lumen Area (mm²)',
      'Minimum Lumen Diameter (mm)',
      'Plaque Burden (%)',
    ].map((label) => ({ label, values: ['-'] })),
  };
  const tableRows =
    BranchId != null &&
    pmdNode.proximal >= 0 &&
    pmdNode.mid >= 0 &&
    pmdNode.distal >= 0
      ? (() => {
          const result = branchIds.find((obj) =>
            Object.prototype.hasOwnProperty.call(obj, BranchId),
          );
          if (!result) return plaqueFallbackRows;

          const [, label] = Object.entries(result)[0];
          void label;

          const data = dataset?.getPlaqueTotalInfo(
            BranchId!,
            pmdNode.proximal,
            pmdNode.mid,
            pmdNode.distal,
          );
          if (!data) return plaqueFallbackRows;

          return {
            columns: [`Metric`, 'Value'],
            rows: [
              { label: 'Length (mm)', values: [fmt(data.lengthValue)] },
              { label: 'Lumen Volume (mm³)', values: [fmt(data.lumenValue)] },
              { label: 'Plaque Volume (mm³)', values: [fmt(data.plaqueValue)] },
              { label: 'Vessel Volume (mm³)', values: [fmt(data.vesselValue)] },
              {
                label: 'Calcified Plaque Volume (mm³, %)',
                values: [pct(data.calcifiedValue, data.vesselValue)],
              },
              {
                label: 'Noncalcified Plaque Volume (mm³, %)',
                values: [pct(data.noncalcifiedValue, data.vesselValue)],
              },
              {
                label: 'Low Attenuation Plaque Volume (mm³, %)',
                values: [pct(data.lowattenuationValue, data.vesselValue)],
              },
              {
                label: 'Remodeling Index',
                values: [fmt(data.remodelingIndex)],
              },
              // {
              //   label: 'Stenosis Degree (%)',
              //   values: [fmt((1 - (data.stenosisDegree ?? NaN)) * 100)],
              // },
              {
                label: 'Minimum Lumen Area (mm²)',
                values: [fmt(data.minLumenArea)],
              },
              {
                label: 'Minimum Lumen Diameter (mm)',
                values: [fmt(data.minLumenDiameter)],
              },
              {
                label: 'Plaque Burden (%)',
                values: [fmt(data.plaqueburden * 100)],
              },
            ],
          };
        })()
      : plaqueFallbackRows;

  const divRef = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (!divRef.current) return;

    const currentDiv = divRef.current;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.contentRect) {
          const { width, height } = entry.contentRect;
          if (width !== dimensions.width || height !== dimensions.height) {
            setDimensions({ width, height });
          }
        }
      });
    });

    observer.observe(currentDiv);

    return () => {
      observer.disconnect();
    };
  }, []);

  const memoizedClassName = useMemo(
    () =>
      `panel ${className ?? ''}${
        fullscreenPanelComponentKey === panelConfig.componentKey
          ? ' fullscreen'
          : ''
      }`,
    [fullscreenPanelComponentKey, className, panelConfig.componentKey],
  );

    const { stenosisSegmentRows, stenosisSegmentTargets } = useMemo(() => {
    const defaultRows: SegmentRow[] = [
      { vessel: 'LM', proximal: '', mid: '0', distal: '' },
      { vessel: 'LAD', proximal: '0', mid: '0', distal: '0' },
      { vessel: 'LCX', proximal: '0', mid: '-', distal: '0' },
      { vessel: 'RCA', proximal: '0', mid: '0', distal: '0' },
    ];
    const defaultTargets: SegmentTargets = {};

    const paramData = pool?.parameter.data as CalciumAnalysisResult | undefined;
    const lesioninfo =
      (config.stenosisLesionInfo as number[][][] | undefined) ?? undefined;
    const name = paramData?.SavedInfo?.Coronary?.newType;
    if (!dataset || !Array.isArray(name) || !Array.isArray(lesioninfo)) {
      return {
        stenosisSegmentRows: defaultRows,
        stenosisSegmentTargets: defaultTargets,
      };
    }

    const branchMap = new Map<string, number>();
    name.forEach((entry) => {
      const [branch, index] = entry as [string, number];
      if (typeof branch === 'string' && typeof index === 'number') {
        branchMap.set(branch, index);
      }
    });

    const formatPercent = (fraction?: number) => {
      if (typeof fraction !== 'number' || !Number.isFinite(fraction)) {
        return '0';
      }
      const percent = Math.max(0, fraction);
      const rounded = Math.round(percent * 10) / 10;
      const fixed = rounded.toFixed(1);
      return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
    };

    const getTotalLength = (index: number) => {
      const arr = dataset.getLengthArray(index);
      if (!Array.isArray(arr) || arr.length === 0) return 0;
      return arr[arr.length - 1];
    };

    const targets: SegmentTargets = {};
    const buildRow = (branch: string): SegmentRow => {
      const index = branchMap.get(branch);
      const emptyRow =
        branch === 'LM'
          ? { vessel: 'LM', proximal: '', mid: '0', distal: '' }
          : branch === 'LCX'
            ? { vessel: 'LCX', proximal: '0', mid: '-', distal: '0' }
            : { vessel: branch, proximal: '0', mid: '0', distal: '0' };

      if (index === undefined || index < 0) return emptyRow;
      const lesions = Array.isArray(lesioninfo[index]) ? lesioninfo[index] : [];
      if (lesions.length === 0) return emptyRow;

      let maxFraction = 0;
      const bucket = { proximal: 0, mid: 0, distal: 0 };
      const bucketIndex = { proximal: -1, mid: -1, distal: -1 };
      const totalLength = getTotalLength(index);

      lesions.forEach((lesion, lesionIndex) => {
        const start = typeof lesion[0] === 'number' ? lesion[0] : 0;
        const end = typeof lesion[1] === 'number' ? lesion[1] : 0;
        const fraction = typeof lesion[2] === 'number' ? lesion[3] : 0;
        if (fraction > maxFraction) maxFraction = fraction;

        if (branch === 'LM') return;
        if (totalLength <= 0) return;

        const center = Math.floor((start + end) * 0.5);
        const currentLength = dataset.getLengthValue(index, center);
        const length =
          typeof currentLength === 'number' && Number.isFinite(currentLength)
            ? currentLength
            : 0;
        let segment: 'proximal' | 'mid' | 'distal' = 'proximal';
        if (branch === 'LCX') {
          segment = length < totalLength / 2 ? 'proximal' : 'distal';
        } else {
          const third = totalLength / 3;
          if (length < third) segment = 'proximal';
          else if (length < third * 2) segment = 'mid';
          else segment = 'distal';
        }
        if (fraction > bucket[segment]) {
          bucket[segment] = fraction;
          bucketIndex[segment] = lesionIndex;
        }
      });

      if (branch === 'LM') {
        return { vessel: 'LM', proximal: '', mid: '0', distal: '' };
      }

      if (totalLength <= 0) {
        if (branch === 'LCX') {
          const value = formatPercent(maxFraction);
          return { vessel: 'LCX', proximal: value, mid: '-', distal: value };
        }
        const value = formatPercent(maxFraction);
        return { vessel: branch, proximal: value, mid: value, distal: value };
      }

      if (branch === 'LCX') {
        targets[branch] = {
          proximal:
            bucketIndex.proximal >= 0 ? bucketIndex.proximal : undefined,
          distal: bucketIndex.distal >= 0 ? bucketIndex.distal : undefined,
        };
        return {
          vessel: 'LCX',
          proximal: formatPercent(bucket.proximal),
          mid: '-',
          distal: formatPercent(bucket.distal),
        };
      }

      targets[branch] = {
        proximal: bucketIndex.proximal >= 0 ? bucketIndex.proximal : undefined,
        mid: bucketIndex.mid >= 0 ? bucketIndex.mid : undefined,
        distal: bucketIndex.distal >= 0 ? bucketIndex.distal : undefined,
      };
      return {
        vessel: branch,
        proximal: formatPercent(bucket.proximal),
        mid: formatPercent(bucket.mid),
        distal: formatPercent(bucket.distal),
      };
    };

    const baseRows = ['LM', 'LAD', 'LCX', 'RCA'].map(buildRow);
    const nextRows = previewOverride
      ? baseRows.map((row) =>
          row.vessel === previewOverride.branch
            ? { ...row, [previewOverride.segment]: previewOverride.value }
            : row,
        )
      : baseRows;
    return {
      stenosisSegmentRows: nextRows,
      stenosisSegmentTargets: targets,
    };
  }, [
    config.stenosisLesionInfo,
    dataset,
    pool?.parameter.data,
    previewOverride,
  ]);

  const lines = useMemo(
    () => splitNarrativeLines(config.stenosisResult),
    [config.stenosisResult],
  );
  const handleSegmentSelect = (selection: SegmentSelection | null) => {
    skipPreviewRef.current = 1;
    setSelectedSegment(selection);
    setPreviewOverride(null);
  };
  const handleSegmentSave = (selection: SegmentSelection) => {
    if (!dataset) return;
    const branchId = branchIdByName.get(selection.branch);
    if (branchId == null) return;
    if (pmdNode.proximal < 0 || pmdNode.mid < 0 || pmdNode.distal < 0) {
      return;
    }
    const data = dataset.getPlaqueTotalInfo(
      branchId,
      pmdNode.proximal,
      pmdNode.mid,
      pmdNode.distal,
    );
    const stenosisDegree = data?.stenosisDegree;
    if (
      typeof stenosisDegree !== 'number' ||
      !Number.isFinite(stenosisDegree)
    ) {
      return;
    }

    const lesion = config.stenosisLesionInfo;
    if (
      !Array.isArray(lesion) ||
      !Array.isArray(lesion[branchId]) ||
      !Array.isArray(lesion[branchId][selection.targetIndex])
    ) {
      return;
    }
    const nextLesion = lesion.map((branch) =>
      Array.isArray(branch)
        ? branch.map((entry) => (Array.isArray(entry) ? [...entry] : entry))
        : branch,
    );
    const info = nextLesion[branchId][selection.targetIndex] as number[];
    const plaque = typeof info[4] === 'number' ? info[4] : -1;
    const percentFraction = Math.max(0, Math.abs(1 - stenosisDegree) * 100);
    if (!Number.isFinite(percentFraction)) return;
    nextLesion[branchId][selection.targetIndex] = [
      pmdNode.proximal,
      pmdNode.mid,
      pmdNode.distal,
      percentFraction,
      plaque,
    ];

    const paramData = pool?.parameter.data as CalciumAnalysisResult | undefined;
    if (paramData) {
      const stenosisResult = calcStenosisText(paramData, nextLesion, dataset);
      setData({
        stenosisResult,
        stenosisLesionInfo: nextLesion,
      });
    } else {
      setData({ stenosisLesionInfo: nextLesion });
    }
    setPreviewOverride(null);

    scheduleRender('Surface3D', {
      type: 'stenosisUpdate',
      nextLesion,
    });

    scheduleRender('VR', {
      type: 'stenosisUpdate',
      nextLesion,
    });
  };

  const isrTableRow = useMemo(() => {
    let defalutdata = {
      columns: ['Length', 'ISR(>=50)'],
      rows: [
        { label: '', values: ['', ''] },
        { label: '', values: ['', ''] },
        { label: '', values: ['', ''] },
      ],
    };
    if (BranchId === undefined) return defalutdata;
    let data = config.isr_data;
    if (!data) {
      return defalutdata;
    }

    defalutdata.rows.forEach((rowdata, idx) => {
      const isrdata = data[idx]?.values?.();
      if (!isrdata) return;
      for (const v of isrdata) {
        rowdata.label = v.name;
        rowdata.values = [`${v.length}`, v.isr];
      }
    });
    return defalutdata;
  }, [config.isr_data, BranchId]);

  const onISRButton = async (index: number, isbutton: boolean) => {
    if (!BranchData || BranchId === undefined || !selectedSeriesId) return;

    const curr = config.isr_data;
    const nextArr = [...curr]; // 새 배열

    if (isbutton) {
      if (curr[index].size === 0) {
        waitingAction({ message: 'Calculating' });
        const result = await isr_function(
          accessToken,
          selectedSeriesId,
          BranchId,
          BranchData[0].index - config.coronary_front_expand,
          BranchData[2].index - config.coronary_front_expand,
        );

        let length = 0;
        if (dataset) {
          const len1 = dataset.getLengthValue(
            BranchId,
            BranchData[0].index - config.coronary_front_expand,
          );
          const len2 = dataset.getLengthValue(
            BranchId,
            BranchData[2].index - config.coronary_front_expand,
          );
          length = Math.floor(Math.abs(len1 - len2) * 100) / 100;
        }

        const paramData = pool?.parameter.data as CalciumAnalysisResult;
        const name =
          paramData?.SavedInfo?.Coronary?.newType?.[BranchId]?.[0] ?? '';
        const prevMap = curr[index];
        const nextMap = new Map<number, ISRDataProp>(prevMap); // 새 Map

        nextMap.set(BranchId, {
          name,
          length,
          isr: result.isr_result ? 'True' : 'False',
          index: [BranchData[0].index, BranchData[2].index],
          branchid: BranchId,
        });

        nextArr[index] = nextMap;
        setData({ isr_data: nextArr });
        closeLoadingModal();
      } else {
        nextArr[index] = new Map<number, ISRDataProp>();
        setData({ isr_data: nextArr });
      }
    } else {
      if (curr[index].size !== 0) {
        const keys = Array.from(curr[index].keys());
        const k0 = keys[0];
        const sel = curr[index].get(k0);
        if (!sel) return;

        const mid = Math.floor((sel.index[0] + sel.index[1]) / 2);
        const arr = [sel.index[0], mid, sel.index[1]];

        updateCPRState({ mainIndex: { index: 0, branchId: Number(k0) } });

        [0, 1, 2].forEach((dragIndex, i) => {
          updateCPRState({
            indexedState: {
              mainIndex: { branchId: Number(k0), index: dragIndex },
              pickPoint: { index: arr[i] },
            },
          });
        });

        requestAnimationFrame(() => {
          MarkerLinesMoveRender(0, Number(k0), [0, 1, 2], arr, pool!);
        });
      }
    }
  };

  const onISRAction = async (
    action: 'select' | 'add' | 'remove',
    index?: number,
  ) => {
    const curr = config.isr_data;
    if (!Array.isArray(curr)) return;

    if (action === 'select') {
      if (typeof index !== 'number' || index < 0) return;
      await onISRButton(index, false);
      return;
    }

    if (action === 'remove') {
      if (typeof index !== 'number' || index < 0 || !curr[index]) return;
      const nextArr = [...curr];
      nextArr[index] = new Map<number, ISRDataProp>();
      setData({ isr_data: nextArr });
      return;
    }

    // add: fill first empty slot only (do not toggle existing row)
    const targetIndex = curr.findIndex((m) => m?.size === 0);
    if (targetIndex < 0) return;
    await onISRButton(targetIndex, true);
  };

  return (
    <div
      className={memoizedClassName}
      onClick={() => {
        updateSelectedPanelConfig({
          ...panelConfig,
          componentKey: panelConfig.componentKey,
        });
      }}
    >
      <div className="renderer" ref={divRef}>
        {analysisType === 'stenosis' && (
          <StenosisTable
            width={dimensions.width}
            height={dimensions.height}
            segmentRows={stenosisSegmentRows}
            segmentTargets={stenosisSegmentTargets}
            data={lines}
            onSegmentSelect={handleSegmentSelect}
            onSegmentSave={handleSegmentSave}
          />
        )}
        {analysisType === 'plaque' && tableRows && (
          <PlaqueTable
            width={dimensions.width}
            height={dimensions.height}
            columns={tableRows.columns}
            rows={tableRows.rows}
          />
          // <ReportTable
          //   width={dimensions.width}
          //   height={dimensions.height}
          //   columns={tableRows.columns}
          //   rows={tableRows.rows}
          // />
        )}
        {analysisType === 'isr' && isrTableRow && (
          <ISRReportTable rows={isrTableRow.rows} onAction={onISRAction} />
        )}
        {analysisType === 'tavi' && (
          <TaviReportTable sections={taviSections} />
        )}
      </div>
      {/* {globalToolState?.toggles.ToggleAnnotations && analysisType !== 'tavi' && (
        <>
          <PanelOverlay
            config={{}}
            originComponentKey={panelConfig.componentKey}
          />
        </>
      )} */}
    </div>
  );
};

export default ReportPanel;
